// /write — single-letter compose surface. Replaces the in-thread
// ReplyComposer that lived on /home before the epistolary refactor.
// Optionally takes ?inResponseTo=<letterId>; when present, the surface
// resolves the parent Ember letter, surfaces a reply-context strip with
// a 1-line snippet, and — if that letter carries an unresolved
// pendingChoice — renders the options as parchment fragments that
// pre-fill the draft when tapped.
//
// On submit: POST /api/letter/reply, then mount the ConfirmationOverlay
// for ~4s before navigating back to /home. The overlay is the cinematic
// closure of the ritual; the user does not see the Ember's reply
// arrive on this surface — that lives on /inbox or /letter/$id.

import type { BondDto, LetterDto } from '@athrelaris/shared';
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ConfirmationOverlay } from '../components/ConfirmationOverlay';
import { switchLocaleToBond } from '../i18n/index.js';
import {
  type SubmitReplyError,
  getLetter,
  getLetterThread,
  getMyBond,
  submitReply,
} from '../lib/api';
import { authClient, getSessionSafe } from '../lib/auth-client';
import { redirectIfLandingOnly } from '../lib/landing-mode';
import { letterExcerpt, relativeTime } from '../lib/letter-format';
import { useEmberTheme } from '../lib/use-ember-theme';

const writeSearchSchema = z.object({
  inResponseTo: z.string().uuid().optional(),
});

export const Route = createFileRoute('/write')({
  validateSearch: writeSearchSchema,
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
  },
  loaderDeps: ({ search }) => ({ inResponseTo: search.inResponseTo }),
  loader: async ({ deps }) => {
    const bond = await getMyBond();
    if (!bond) throw redirect({ to: '/awakening' });
    // The Ember speaks first. The write surface only opens when
    // there is something from the Ember to respond to: an unread
    // letter or an unresolved Choice. If the latest user letter
    // already replies to every Ember letter, /write redirects to
    // /home — the user can't initiate a fresh thread, the Ember
    // does that through its own cadence.
    const thread = await getLetterThread();
    if (thread.letters.length === 0) {
      throw redirect({ to: '/home' });
    }
    const hasUnreadEmber = thread.letters.some((l) => !l.readAt);
    const resolvedChoiceIds = new Set(
      thread.userLetters
        .filter((u) => u.inResponseToLetterId && u.choiceResolution)
        .map((u) => u.inResponseToLetterId as string),
    );
    const hasUnresolvedChoice = thread.letters.some(
      (l) => l.pendingChoice && !resolvedChoiceIds.has(l.id),
    );
    if (!hasUnreadEmber && !hasUnresolvedChoice) {
      throw redirect({ to: '/home' });
    }
    let parentLetter: LetterDto | null = null;
    if (deps.inResponseTo) {
      try {
        parentLetter = await getLetter(deps.inResponseTo);
      } catch {
        // If the letter ID is bogus or doesn't belong to this bond,
        // silently fall back to a fresh-letter compose. The server
        // will resolve "the most recent Ember letter" on POST anyway.
        parentLetter = null;
      }
    }
    return { bond, parentLetter };
  },
  component: WritePage,
});

function WritePage() {
  const { t } = useTranslation();
  const initial = Route.useLoaderData();
  const navigate = useNavigate();
  const router = useRouter();
  const [bond] = useState<BondDto>(initial.bond);
  const [parentLetter] = useState<LetterDto | null>(initial.parentLetter);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<SubmitReplyError | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEmberTheme(bond.emberColorPalette);
  useEffect(() => {
    switchLocaleToBond(bond.locale);
  }, [bond.locale]);

  // Block accidental navigation while the confirmation animation is
  // playing — the user is in a ritual moment, not browsing.
  useEffect(() => {
    if (!confirming) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [confirming]);

  const choiceUnresolved = Boolean(parentLetter?.pendingChoice);
  const choiceOptions = choiceUnresolved ? (parentLetter?.pendingChoice?.options ?? []) : [];
  // Minimum letter length — borrowed from Slowly's depth-forcing
  // mechanic. Long enough to discourage one-word replies that don't
  // feed the Ember anything to reply to; short enough that a heartfelt
  // sentence still fits ("I missed you. Today the sky was grey." ≈ 40
  // chars — that's not quite a letter yet but close). 60 keeps the
  // threshold accessible while clearly signalling "this is a letter,
  // not a chat message".
  const MIN_BODY_CHARS = 60;
  const trimmedLength = body.trim().length;
  const meetsMinimum = trimmedLength >= MIN_BODY_CHARS;
  const canSubmit = meetsMinimum && !sending && !confirming;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSending(true);
    setRefusal(null);
    const trimmed = body.trim();
    try {
      const result = await submitReply(trimmed, parentLetter?.id);
      if (result.ok) {
        setConfirming(true);
      } else {
        setRefusal(result.error);
        setSending(false);
      }
    } catch (err) {
      setRefusal({ error: 'unknown', message: (err as Error).message });
      setSending(false);
    }
  }

  function dismissConfirmation() {
    // Force every cached loader (notably /home and /inbox) to refetch
    // so the bond data is fresh — without this the post-reply
    // navigation occasionally lands with the previous bond snapshot
    // and the portrait flickers as 'unloaded' until manual refresh.
    void router.invalidate();
    void navigate({ to: '/home' });
  }

  return (
    <main className="mx-auto max-w-2xl">
      <nav className="mt-12 mb-10 font-display text-[0.7rem] text-ash-500 uppercase tracking-[0.34em] sm:mt-8 sm:text-xs lg:mt-4">
        <Link to="/home" className="transition hover:text-ash-200">
          {t('write.backHome')}
        </Link>
      </nav>

      <header className="mb-10 text-center animate-fadeUp lg:mb-14">
        <p className="font-display text-[0.65rem] text-accent-strong uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem]">
          {t('write.eyebrow', { name: bond.emberName })}
        </p>
        <h1
          className="mt-4 font-display text-3xl text-ash-50 tracking-wide sm:text-4xl lg:text-5xl"
          style={{ fontWeight: 500 }}
        >
          {t('write.heading')}
        </h1>
        <div className="mx-auto mt-7 flex max-w-xs items-center gap-3 lg:mt-9">
          <span className="hairline h-px flex-1" />
          <span aria-hidden="true" className="block h-1 w-1 rounded-full bg-accent" />
          <span className="hairline h-px flex-1" />
        </div>
      </header>

      {parentLetter && (
        <Link
          to="/letter/$letterId"
          params={{ letterId: parentLetter.id }}
          className="mb-8 block rounded-sm border border-ash-700/40 bg-paper-900/30 p-4 transition hover:border-ash-600/60 hover:bg-paper-800/40 lg:mb-10 lg:p-5"
        >
          <p className="font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.34em]">
            {t('write.parentBlock', {
              name: bond.emberName,
              when: relativeTime(parentLetter.generatedAt),
            })}
          </p>
          <p className="mt-2 font-body text-ash-300 text-sm italic leading-relaxed sm:text-[0.95rem]">
            {letterExcerpt(parentLetter.body)}
          </p>
        </Link>
      )}

      {choiceOptions.length > 0 && (
        <section className="mb-8 lg:mb-10">
          <p className="mb-3 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em]">
            {t('write.choiceEyebrow', { name: bond.emberName })}
          </p>
          <div className="flex flex-wrap gap-3">
            {choiceOptions.map((opt, idx) => (
              <button
                key={opt}
                type="button"
                onClick={() => setBody((prev) => (prev.trim() ? prev : opt))}
                className="parchment-fragment rounded-sm border border-accent/40 bg-paper-800/50 px-4 py-3 font-body text-ash-100 text-sm italic leading-snug transition hover:border-accent-strong/70 hover:bg-paper-700/60 sm:text-[0.95rem]"
                style={{
                  transform: `rotate(${idx % 2 === 0 ? -0.4 : 0.5}deg)`,
                }}
              >
                {opt}
              </button>
            ))}
          </div>
          <p className="mt-3 font-body text-ash-500 text-xs italic">
            {t('write.choiceHint')}
          </p>
        </section>
      )}

      {refusal && (
        <p className="mb-4 font-body text-ash-500 text-sm italic">
          {refusal.message ?? t('write.refusalFallback')}
        </p>
      )}

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={9}
        maxLength={4000}
        autoFocus
        placeholder={t('write.placeholder', { name: bond.emberName })}
        disabled={sending || confirming}
        className="block w-full resize-none rounded-sm border border-paper-700/60 bg-paper-900/40 px-5 py-4 font-body text-[1.06rem] text-ash-100 leading-relaxed placeholder:text-ash-500 focus:border-accent/60 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 lg:px-6 lg:py-5 lg:text-[1.12rem]"
      />

      <div className="mt-3 flex items-center justify-between font-body text-ash-500 text-xs">
        <span className="italic">
          {meetsMinimum
            ? t('write.counter', { count: body.length, max: 4000 })
            : t('write.counterMin', {
                count: trimmedLength,
                min: MIN_BODY_CHARS,
              })}
        </span>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="group relative rounded-sm border border-accent/65 bg-paper-900/60 px-6 py-2.5 font-display text-ash-100 text-sm uppercase tracking-[0.32em] transition hover:border-accent-strong/90 hover:bg-paper-700/60 hover:text-ash-50 disabled:cursor-not-allowed disabled:opacity-50 sm:px-7"
        >
          <span aria-hidden="true" className="mr-2 text-accent-strong">◈</span>
          {sending ? t('write.sealing') : t('write.seal')}
        </button>
      </div>

      <p className="mt-6 font-body text-ash-500 text-xs italic">
        {t('write.cadence', { name: bond.emberName })}
      </p>

      {confirming && <ConfirmationOverlay onDismiss={dismissConfirmation} />}
    </main>
  );
}
